import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import StraightenOutlinedIcon from "@mui/icons-material/StraightenOutlined";
import { Box, Chip, Stack, Typography } from "@mui/material";
import { useState } from "react";

import type { SectionSpec } from "@/shared/models/SectionSpec";
import { formatSectionSpecRevision } from "@/shared/utils/sectionSpecRevision";
import { SECTION_SPEC_FILE_KIND } from "@/shared/models/SectionSpec";
import { useSectionDrawingPreviewQuery } from "@/pages/products/test-shack/hooks/queries/useSectionDrawingPreviewQuery";
import {
	formatSpecValue as num,
	formatWeightBand,
	SectionDetailStat,
	SectionDrawingCard,
	SectionDrawingDialog,
	sectionSpecEmptyState,
} from "@/pages/products/test-shack/shared/components/SectionDetailsPanelContent";

const DRAWING_KIND = {
	MILL: "mill",
	PROFILE: "profile",
} as const;

type DrawingKind = (typeof DRAWING_KIND)[keyof typeof DRAWING_KIND];

interface SheetSectionDetailsPanelProps {
	sectionId: number | null;
	spec: SectionSpec | null;
	hasConfirmedRevisions: boolean;
	pending?: boolean;
}

export const SheetSectionDetailsPanel = ({
	sectionId,
	spec,
	hasConfirmedRevisions,
	pending = false,
}: SheetSectionDetailsPanelProps) => {
	const [openedKind, setOpenedKind] = useState<DrawingKind | null>(null);
	const millPreview = useSectionDrawingPreviewQuery({
		fileId: spec?.MillDrawingUploadFileId ?? null,
		fileName: spec?.MillDrawingFileName ?? null,
	});
	const profilePreview = useSectionDrawingPreviewQuery({
		fileId: spec?.DimensionsReferenceUploadFileId ?? null,
		fileName: spec?.DimensionsReferenceFileName ?? null,
	});

	const emptyState = sectionSpecEmptyState({
		sectionId,
		spec,
		pending,
		hasConfirmedRevisions,
	});
	if (emptyState || !spec) return emptyState;

	const millTitle = spec.PrintNo ? `Drawing ${spec.PrintNo}` : "Mill drawing";
	const weightBand = formatWeightBand(spec);

	return (
		<Stack spacing={1.5} sx={{ height: "100%", minHeight: 0 }}>
			<Stack
				direction="row"
				spacing={1}
				alignItems="center"
				flexWrap="wrap"
				useFlexGap
				flexShrink={0}
			>
				<Typography variant="subtitle2" fontWeight={700}>
					Section details
				</Typography>
				<Chip
					size="small"
					color="primary"
					label={formatSectionSpecRevision(spec.Rev, spec.SubRev)}
				/>
				<Typography variant="body2" color="text.secondary">
					{spec.SectionDescription ?? spec.SectionName}
				</Typography>
			</Stack>

			<Box
				sx={{
					display: "grid",
					flexShrink: 0,
					gap: 1,
					gridTemplateColumns: {
						xs: "repeat(2, minmax(0, 1fr))",
						lg: "repeat(4, minmax(0, 1fr))",
					},
				}}
			>
				<SectionDetailStat label="Print no." value={spec.PrintNo ?? "-"} />
				<SectionDetailStat label="Area sq.in." value={num(spec.Area)} />
				<SectionDetailStat
					label="Leg delta max"
					value={num(spec.LegDeltaMax)}
				/>
				<SectionDetailStat
					label="Weight lb/ft"
					value={num(spec.TargetLbsPerFoot)}
					hint={spec.TargetLbsPerFoot === null ? null : weightBand}
				/>
			</Box>

			<Box
				sx={{
					display: "grid",
					flex: 1,
					gap: 1.5,
					gridTemplateColumns: {
						xs: "minmax(0, 1fr)",
						lg: "repeat(2, minmax(0, 1fr))",
					},
					minHeight: { xs: 0, lg: 280 },
				}}
			>
				<SectionDrawingCard
					key={spec.MillDrawingUploadFileId ?? "missing-mill-drawing"}
					spec={spec}
					kind={SECTION_SPEC_FILE_KIND.MILL_DRAWING}
					fileName={spec.MillDrawingFileName}
					title="Mill drawing"
					icon={<DescriptionOutlinedIcon fontSize="small" />}
					missingMessage="Mill drawing not uploaded yet"
					preview={millPreview}
					onOpen={() => setOpenedKind(DRAWING_KIND.MILL)}
				/>
				<SectionDrawingCard
					key={
						spec.DimensionsReferenceUploadFileId ?? "missing-reference-image"
					}
					spec={spec}
					kind={SECTION_SPEC_FILE_KIND.DIMENSIONS_REFERENCE}
					fileName={spec.DimensionsReferenceFileName}
					title="Dimensions reference"
					icon={<StraightenOutlinedIcon fontSize="small" />}
					missingMessage="Reference image not uploaded yet"
					preview={profilePreview}
					onOpen={() => setOpenedKind(DRAWING_KIND.PROFILE)}
				/>
			</Box>

			{openedKind !== null && (
				<SectionDrawingDialog
					title={
						openedKind === DRAWING_KIND.MILL
							? millTitle
							: "Dimensions reference"
					}
					label={
						openedKind === DRAWING_KIND.MILL
							? "Mill drawing"
							: "Dimensions reference"
					}
					preview={
						openedKind === DRAWING_KIND.MILL ? millPreview : profilePreview
					}
					onClose={() => setOpenedKind(null)}
				/>
			)}
		</Stack>
	);
};
